/**
 * LegalDoc — public in-app legal document viewer.
 *
 * Fetches document content from GET /api/legal/{document_id} and renders
 * the returned markdown. Public route (no auth required — reached by users
 * mid-activation who aren't yet logged in).
 *
 * Document IDs:
 *   terms                 → Terms of Service        (/legal/terms)
 *   privacy               → Privacy Policy          (/legal/privacy)
 *   dpa                   → Data Processing Addendum (/legal/dpa)
 *   aup                   → Acceptable Use Policy  (/legal/acceptable-use)
 *   account_privacy_notice → Account-Data Privacy Notice (/legal/account-data-privacy)
 *
 * API response shape: { document_id, version, action_type, content }
 *   `content` is markdown — rendered via EvaluationMarkdown (react-markdown +
 *   remark-gfm, same security posture as the AI eval surface).
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { EvaluationMarkdown } from "@/components/candidate-page/EvaluationMarkdown";

// ── API types ─────────────────────────────────────────────────────────────────

type LegalDocResponse = {
  document_id: string;
  version: string;
  action_type: string;
  content: string;
};

// ── Hook ──────────────────────────────────────────────────────────────────────

function useLegalDoc(documentId: string) {
  return useQuery<LegalDocResponse, ApiError>({
    queryKey: ["legal-doc", documentId],
    queryFn: async () => {
      const resp = await apiClient.get<LegalDocResponse>(
        `/api/legal/${encodeURIComponent(documentId)}`,
      );
      return resp.data;
    },
    enabled: Boolean(documentId),
    retry: false,
    // Legal docs are stable — cache for 10 minutes.
    staleTime: 1000 * 60 * 10,
  });
}

// ── Page component ────────────────────────────────────────────────────────────

interface LegalDocPageProps {
  /** The document_id to fetch. Passed as a prop so the router
   *  can hard-wire the right ID without relying on URL param naming. */
  documentId: string;
  /** Human-readable title shown in the header while loading / on error. */
  title: string;
}

export function LegalDocPage({ documentId, title }: LegalDocPageProps): JSX.Element {
  const { data, isLoading, isError, error } = useLegalDoc(documentId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-canvas px-6 py-12">
        <div className="mx-auto max-w-[720px]">
          <p className="text-sm text-text-secondary dark:text-brand-100/70">Loading…</p>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    const msg =
      error?.error === "not_found"
        ? "This document is not available. Please contact support."
        : "Unable to load this document. Please try again.";
    return (
      <div className="min-h-screen bg-surface-canvas px-6 py-12">
        <div className="mx-auto max-w-[720px]">
          <h1 className="mb-4 text-xl font-semibold text-text-primary">{title}</h1>
          <p className="text-sm text-text-secondary dark:text-brand-100/70">{msg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas px-6 py-12">
      <div className="mx-auto max-w-[720px]">
        {/* Document header */}
        <div className="mb-8 border-b border-border-default pb-6">
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-accent-secondary">
            CertAIn HR, LLC
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">{title}</h1>
          <p className="mt-2 text-xs text-text-tertiary">Version {data.version}</p>
        </div>

        {/* Markdown content — same secure renderer as AI evaluation output */}
        <div data-testid="legal-doc-content">
          <EvaluationMarkdown source={data.content} />
        </div>
      </div>
    </div>
  );
}

// ── Thin route wrappers (imported individually by the router) ─────────────────

export function TermsOfServicePage(): JSX.Element {
  return <LegalDocPage documentId="terms" title="Terms of Service" />;
}

export function PrivacyPolicyPage(): JSX.Element {
  return <LegalDocPage documentId="privacy" title="Privacy Policy" />;
}

export function DataProcessingAddendumPage(): JSX.Element {
  return <LegalDocPage documentId="dpa" title="Data Processing Addendum" />;
}

export function AcceptableUsePolicyPage(): JSX.Element {
  return <LegalDocPage documentId="aup" title="Acceptable Use Policy" />;
}

export function AccountDataPrivacyNoticePage(): JSX.Element {
  return (
    <LegalDocPage documentId="account_privacy_notice" title="Account-Data Privacy Notice" />
  );
}
